/**
 * Pure presentation helpers for the raid-calendar grid. Lives in
 * `composables/` (a new subfolder scoped to the gvg-planner feature
 * — see `wwm-gvg/AGENTS.md`'s future Phase 4 split) so the
 * 7-day grid math is reachable from `<CalendarGrid />`,
 * `<RaidCalendarView />`, and its tests without dragging in a Vue
 * component instance.
 *
 * Phase 0 keeps the feature under `features/gvg-planner/`; Phase 4
 * moves shared composables into `src/composables/`. Until then, this
 * colocated helper is the right home.
 *
 * The contract surface mirrors what the calendar grid needs from the
 * view: grid geometry, slot math (pointer→slot, day-index lookup,
 * drag/drop slot helpers). All functions are pure — no `ref()`,
 * no `computed()`, no mutation. The pin to the existing
 * `lib/calendarFormatters.ts` constants keeps the math consistent with
 * the helpers the view already uses (e.g. `raidBlockStyle`,
 * `slotStyle`).
 */

import {
  CALENDAR_DAY_HEIGHT_PX,
  CALENDAR_SLOT_MINUTES,
  slotMinutesAtCalendarY,
} from '../lib/calendarFormatters.ts'

/**
 * CSS-grid `grid-template-columns` value for the 8-column calendar:
 * one column for the sticky time-of-day ruler + seven columns for the
 * day lanes. `minmax(min-content, 1fr)` pins each day lane to its
 * content minimum (≈ 10rem for the readable "15 Jul" header badge +
 * 44px plan/edit targets) while still letting the grid expand into
 * the available width on wide screens.
 */
export function gridColumnTemplate(): string {
  return '4rem repeat(7, minmax(min-content, 1fr))'
}

/**
 * A user-picked calendar slot — a 15-minute cell on a specific day
 * (0–6). `minutes` is the number of minutes after midnight, snapped
 * to `[0, (CALENDAR_SLOTS_PER_DAY - 1) * CALENDAR_SLOT_MINUTES]`
 * i.e. the legal index range for `slotStyle`.
 */
export interface CalendarSlot {
  readonly dayIndex: number
  readonly minutes: number
}

/**
 * One column in the 7-day grid. Mirrors the `RaidCalendarDay` shape
 * from `types/raid.ts` but is structural — the composable does not
 * import the domain types so the helper stays free of `vue` refs.
 */
export interface GridDay {
  readonly date: Date
  readonly raidCount: number
}

/**
 * Convert a pointer event inside an inverted day column into the 15-minute
 * slot it landed on. The top maps to late night and the bottom maps to
 * midnight. Returns `null` when the event has no measurable target (rare —
 * `PointerEvent.currentTarget` is normally set). The caller is responsible
 * for stamping the resulting `dayIndex` from the column's `data-day-index`
 * attribute (set by `<CalendarGrid />`).
 */
export function slotMinutesFromPointer(event: { readonly currentTarget: EventTarget | null; readonly clientY: number }): number | null {
  const target = event.currentTarget as HTMLElement | null
  if (target === null) return null
  const rect = target.getBoundingClientRect()
  const y = event.clientY - rect.top
  return slotMinutesAtCalendarY(y)
}

/**
 * Read `data-day-index` from the event's currentTarget. Returns the
 * clamped integer day index (0-6) or `null` when the attribute is
 * missing/malformed. The view always sets the attribute in the
 * template, so a `null` return is reserved for malformed events.
 */
export function dayIndexFromEvent(event: { readonly currentTarget: EventTarget | null }): number | null {
  const target = event.currentTarget as HTMLElement | null
  if (target === null) return null
  const raw = target.getAttribute('data-day-index')
  if (raw === null) return null
  const parsed = Number.parseInt(raw, 10)
  if (!Number.isInteger(parsed) || parsed < 0 || parsed > 6) return null
  return parsed
}

/**
 * Convert a `(day, slotMinutes)` pair into the absolute `Date` a
 * click-to-create planner should pre-fill. Caller passes the date
 * from `week.days[dayIndex]?.date`. Returns `null` when the day is
 * out of range so the view can fall back to "now" without crashing.
 */
export function dateForSlot(day: Date | undefined, minutes: number): Date | null {
  if (day === undefined) return null
  const start = new Date(day)
  start.setHours(0, 0, 0, 0)
  start.setMinutes(Math.max(0, Math.min(23 * 60 + 45, minutes)))
  // Snap to the nearest quarter-hour — the planner grid is 15-min
  // granular and the view's `slotAtPointer` already snaps on the way
  // in, so this is defensive for any external caller that didn't.
  const quarters = Math.round(start.getMinutes() / CALENDAR_SLOT_MINUTES)
  start.setMinutes(quarters * CALENDAR_SLOT_MINUTES, 0, 0)
  return start
}

/**
 * CSS height for a day column — always the full 24h slot stack so
 * the absolute-positioned hour grid lines + raid blocks line up.
 * Mirrors `rulerStyle` in the original view.
 */
export function dayColumnStyle(): Readonly<Record<string, string>> {
  return { height: `${String(CALENDAR_DAY_HEIGHT_PX)}px` }
}
